import {
  DateTimeInput,
  Form,
  required,
  SaveButton,
  SelectInput,
  TextInput,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
  usePermissions,
  useGetList,
} from 'react-admin';
import {
  Box,
  Card,
  Divider,
  Stack,
  Typography,
  Button as MUIButton,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Permission, PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';
import useAgentInfo from './useAgentInfo';
import { Resources } from '../resource';

interface FormProps {
  isCreate: boolean;
}

type Row = {
  userId: string;
  userLevel?: string;
  [k: string]: any;
};
const UpgradeAgentForm = (props: FormProps) => {
  const { isCreate } = props;
  const t = useTranslate();
  const redirect = useRedirect();
  const { watch, setValue, trigger } = useFormContext();
  const record = useRecordContext();
  const { findAgentInfo } = useAgentInfo();

  const currentLevelId: number = useWatch({ name: 'agentLevelId' });
  const currentLevelName: string = useWatch({ name: 'userLevel' }) ?? record?.name ?? '';
  const targetLevelId = useWatch({ name: 'targetLevelId' });

  const HIGH_LEVEL_THRESHOLD = 6;
  const { data } = usePermissions<PermissionData>();
  const { permissions = [] } = data || {};
  const isOps = hasPermission(permissions, 'agent-info', 'upgrade-plus');

  const { data: levelRows = [], isLoading } = useGetList<any>(Resources.agents, {
    pagination: { page: 1, perPage: 10 },
    sort: { field: 'id', order: 'ASC' },
    filter: {},
  });

  const levelRank = (name?: string): number => {
    const s = String(name ?? '').trim();
    if (!s) return NaN;
    if (/[∞]/.test(s)) return Infinity;
    const m = s.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : NaN;
  };

  const currentRank = useMemo(() => {
    const r = levelRank(currentLevelName);
    return Number.isNaN(r) ? -Infinity : r;
  }, [currentLevelName]);

  const allLevels = useMemo(
    () =>
      (levelRows || [])
        .map((lvl: any) => {
          const name = String(lvl?.name ?? `Lv${lvl?.id ?? ''}`);
          return {
            id: Number(lvl?.id),
            name,
            rank: levelRank(name),
          };
        })
        .sort((a, b) => {
          if (a.rank === b.rank) return a.id - b.id;
          if (a.rank === Infinity) return 1;
          if (b.rank === Infinity) return -1;
          return a.rank - b.rank;
        }),
    [levelRows]
  );

  // Allowed selection range: Up to Lv5 for ordinary administrators; The operation administrator is not limited to Lv∞
  const allowedChoices = useMemo(() => {
    if (!allLevels.length) return [];
    const maxRank = isOps ? Infinity : 5;
    return allLevels
      .filter((opt) => Number.isFinite(opt.rank) || opt.rank === Infinity)
      .filter((opt) => opt.rank > currentRank && opt.rank <= maxRank)
      .sort((a, b) => (a.rank === b.rank ? 0 : a.rank < b.rank ? -1 : 1));
  }, [allLevels, currentRank, isOps]);

  useEffect(() => {
    if (targetLevelId != null && !allowedChoices.some((c) => c.id === Number(targetLevelId))) {
      setValue('targetLevelId', undefined, { shouldValidate: true, shouldDirty: false });
    }
  }, [allowedChoices, targetLevelId, setValue]);

  // const allLevels = useMemo(
  //   () =>
  //     (levelRows || []).map((lvl: any) => ({
  //       id: Number(lvl?.id),
  //       name: String(lvl?.name ?? `Lv${lvl?.id ?? ''}`),
  //     })),
  //   [levelRows]
  // );

  // const allowedChoices = useMemo(() => {
  //   if (!allLevels.length) return [];
  //   const maxAllow = isOps ? Number.MAX_SAFE_INTEGER : HIGH_LEVEL_THRESHOLD - 1;
  //   return allLevels.filter((opt) => opt.id > currentLevelId && opt.id <= maxAllow);
  // }, [allLevels, currentLevelId, isOps]);

  // useEffect(() => {
  //   if (
  //     targetLevelId !== null &&
  //     targetLevelId !== undefined && !allowedChoices.some((c) => c.id === Number(targetLevelId))
  //   ) {
  //     setValue('targetLevelId', undefined, { shouldValidate: true, shouldDirty: false });
  //   }
  // }, [allowedChoices, targetLevelId, setValue]);

  const noChoice = !isLoading && allowedChoices.length === 0;

  return (
    <>
      <Stack spacing={3} gap={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.userId')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="userId"
              validate={[required()]}
              InputProps={{ readOnly: !isCreate }}
              fullWidth
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.userLevel')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="userLevel"
              validate={[required()]}
              InputProps={{ readOnly: true }}
              fullWidth
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.targetLevel')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <SelectInput
              label={false}
              source="targetLevelId"
              validate={[required()]}
              choices={allowedChoices}
              placeholder={t('module.agentInfo.targetLevel')}
              optionText="name"
              optionValue="id"
              disabled={isLoading || noChoice}
              fullWidth
              helperText={
                isLoading
                  ? t('module.agentInfo.loadingLevels')
                  : noChoice
                    ? t('module.agentInfo.noAvailableUpgrade')
                    : isOps
                      ? t('module.agentInfo.canChooseAnyLevelAboveCurrent')
                      : t('module.agentInfo.canOnlyChooseLevelsUpTo6')
              }
            />
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.reason')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="reason"
              validate={[required()]}
              placeholder={t('module.agentInfo.reason')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="string"
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.effectiveDate')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <DateTimeInput
              label={false}
              source="effectiveDate"
              fullWidth
              validate={[required()]}
              helperText={<span style={{ color: 'red' }}></span>}
            />
          </Box>
        </Stack>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          mb={1}
        >
          <Typography variant="body2" sx={{ flexBasis: '25%', flexShrink: 0 }}>
            {t('module.agentInfo.upgradedBy')}
          </Typography>
          <Box sx={{ flexGrow: 1 }}>
            <TextInput
              label={false}
              source="upgradedBy"
              validate={[required()]}
              placeholder={t('module.agentInfo.upgradedBy')}
              fullWidth
              helperText={<span style={{ color: 'red' }}></span>}
              type="string"
            />
          </Box>
        </Stack>
      </Stack>
    </>
  );
};

export default UpgradeAgentForm;
